import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, defineMessage, useIntl } from 'react-intl';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import { getLeagueAvatar } from '@sorare/marketplace/src/lib/newSeason';

import { WithCompetitionStatusDot } from '#src/components/essence/CardPullExperience/CompetitionAvailability/WithCompetitionStatusDot.tsx';
import { PoolFilterSelect } from '#src/components/rewards/PoolFilterSelect/index.tsx';

import type { PoolCompetitionFilter_competition } from './__generated__/index.graphql.ts';

export const COMPETITION_SEARCH_PARAM = 'competition';
export const ALL_COMPETITIONS = 'all';

const allCompetitions = defineMessage({
  defaultMessage: 'All competitions',
});

const AVATAR_SIZE = 20;

type Props = {
  competitions: PoolCompetitionFilter_competition[];
  /** Selected from another surface (an arena board) before it joined the pool. */
  upcomingCompetition?: PoolCompetitionFilter_competition | null;
  selectedCompetitionSlug: string;
  onChange: (competitionSlug: string) => void;
};

export const PoolCompetitionFilter = ({
  competitions,
  upcomingCompetition,
  selectedCompetitionSlug,
  onChange,
}: Props) => {
  const { formatMessage } = useIntl();

  if (competitions.length < 2 && !upcomingCompetition) {
    return null;
  }

  const competitionOption = (
    competition: PoolCompetitionFilter_competition,
    available: boolean
  ) => {
    const league = getLeagueAvatar({ competition }, 'icon');

    return {
      value: competition.slug,
      label: (
        <Horizontal as="span" gap={1.5}>
          <WithCompetitionStatusDot
            available={available}
            ringColor="var(--c-nd-50-opaque)"
          >
            <LeagueAvatar league={league} size={AVATAR_SIZE} />
          </WithCompetitionStatusDot>
          {league.name}
        </Horizontal>
      ),
    };
  };

  const options = [
    {
      value: ALL_COMPETITIONS,
      label: <FormattedMessage {...allCompetitions} />,
    },
    ...competitions.map(competition => competitionOption(competition, true)),
    ...(upcomingCompetition
      ? [competitionOption(upcomingCompetition, false)]
      : []),
  ];

  return (
    <PoolFilterSelect
      name="crafting-pool-competition"
      header={formatMessage(allCompetitions)}
      options={options}
      value={selectedCompetitionSlug}
      onChange={onChange}
    />
  );
};

PoolCompetitionFilter.fragments = {
  competition: gql`
    fragment PoolCompetitionFilter_competition on Competition {
      slug
      ...getLeagueAvatar_competition
    }
    ${getLeagueAvatar.fragments.competition}
  ` as TypedDocumentNode<PoolCompetitionFilter_competition>,
};
